/**
 * Analytics tracking hook for Google Analytics (GA4) and Meta Pixel
 * Provides helper functions to track page views, events, leads, and form submissions
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

export const useAnalytics = () => {
  /**
   * Track page view
   * @param path - Page path (e.g., '/contact')
   * @param title - Page title
   */
  const trackPageView = (path: string, title?: string) => {
    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
      });
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  };

  /**
   * Track custom event
   * @param name - Event name
   * @param params - Event parameters
   */
  const trackEvent = (name: string, params?: Record<string, unknown>) => {
    // Google Analytics
    if (window.gtag) {
      window.gtag('event', name, params);
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', name, params);
    }
  };

  /**
   * Track lead generation
   * @param source - Lead source (e.g., 'contact_form', 'free_quote', 'chat')
   */
  const trackLead = (source: string) => {
    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'generate_lead', {
        event_category: 'Lead',
        event_label: source,
        value: 1,
      });
    }

    // Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: source,
      });
    }
  };

  /**
   * Track form submission
   * @param formName - Name of the form (e.g., 'Contact Form', 'Free Quote Form')
   */
  const trackFormSubmit = (formName: string) => {
    // Track as both a custom event and a lead
    trackEvent('form_submit', {
      form_name: formName,
    });
    
    trackLead(formName.toLowerCase().replace(/\s+/g, '_'));

    // Google Analytics - Form submission event
    if (window.gtag) {
      window.gtag('event', 'form_submission', {
        event_category: 'Form',
        event_label: formName,
      });
    }

    // Meta Pixel - Contact event
    if (window.fbq) {
      window.fbq('track', 'Contact', {
        content_name: formName,
      });
    }
  };

  return {
    trackPageView,
    trackEvent,
    trackLead,
    trackFormSubmit,
  };
};